import { deviceApi } from "../../actions/devicesInQuery";
import type { AppDispatch, RootState } from "../../reducers";
import type { Device } from "../../types";
import { OFF_STATE } from "../../utils/deviceState";
import type { DeviceStateEnvelope } from "./envelope";

/**
 * How long a realtime transition into OFF is held before it shows.
 *
 * OFF is written by the backend when a device's MQTT last will fires, and a
 * device that only dropped its connection for a moment reconnects and writes
 * its real state again within a second or two. Holding OFF this long - and
 * dropping it if anything newer arrives meanwhile - keeps that false alarm off
 * the screen, at the cost of a real power loss showing two seconds later. Only
 * the realtime path is held: a REST refetch is not a transition, and the
 * server's own status and alerts are not debounced at all.
 */
export const OFF_DEBOUNCE_MS = 2000;

interface Store {
  dispatch: AppDispatch;
  getState: () => RootState;
}

/**
 * Whether an event is newer than what this cached copy already shows.
 *
 * The shadow version orders events exactly. A fetched copy carries it from a
 * backend that sends it, and is otherwise versioned once an event has been
 * patched into it. Until then the shadow timestamp stands in: the backend
 * stores the timestamp of the event that set `state` as `state_updated_at`, so
 * an event before it is one the fetch already reflects. The timestamp only
 * counts whole seconds, so an event in the same second may be the newer one and
 * is taken. A copy with neither - a backend that predates them - takes the
 * event.
 */
const isNewer = (
  device: Partial<Device>,
  event: DeviceStateEnvelope,
): boolean => {
  if (typeof device.state_version === "number") {
    return event.data.version > device.state_version;
  }
  const at = device.state_updated_at
    ? Date.parse(device.state_updated_at)
    : NaN;
  return Number.isNaN(at) || event.occurred_at >= at;
};

const applyTo = (device: Partial<Device>, event: DeviceStateEnvelope) => {
  if (!isNewer(device, event)) return;
  const at = new Date(event.occurred_at).toISOString();
  if (device.status !== event.data.status) {
    device.status = event.data.status as Device["status"];
    // Typed as a Date, but what the API sends - and so what the cache holds -
    // is the ISO string, and the store must stay serializable.
    device.status_updated_at = at as unknown as Date;
  }
  device.state = event.data.state;
  device.state_updated_at = at;
  device.state_version = event.data.version;
};

/**
 * Applies `device.state` events to every cached copy of the device: each
 * listDevices entry (the user's own organization, and every one the superuser
 * panel has opened) and its getDeviceById entry. Patching the cache rather than
 * refetching is what makes the tag change with no round trip.
 *
 * An event for a device in none of them is dropped. A superuser's socket
 * carries every organization's events, and a device that is not on screen has
 * nothing to update; its next fetch brings it in current. An entry on its first
 * load may have read the device before the event, though, and has nothing to
 * patch yet - so while one is loading, the devices are invalidated, which RTK
 * Query holds until the load settles and then refetches. A retry of a load that
 * failed is not a first load, or a failing endpoint would be retried for as
 * long as events keep arriving. Nor is a refetch of an entry that already has
 * data: its merge keeps what was patched, and a device new to the list whose
 * event overtakes the refetch is left to the next fetch or reconnect, like any
 * device not in the cache. Invalidating there would chain, since a superuser's
 * socket carries a steady stream of events for devices it does not hold.
 */
export const createDeviceStateHandler = ({ dispatch, getState }: Store) => {
  const held = new Map<
    string,
    { event: DeviceStateEnvelope; timer: ReturnType<typeof setTimeout> }
  >();

  const listArgsHolding = (uuid: string) => {
    const state = getState();
    return deviceApi.util
      .selectCachedArgsForQuery(state, "listDevices")
      .filter((arg) =>
        deviceApi.endpoints.listDevices
          .select(arg)(state)
          .data?.items.some((d) => d.uuid === uuid),
      );
  };

  const detailHolding = (uuid: string) => {
    const state = getState();
    return (
      deviceApi.util
        .selectCachedArgsForQuery(state, "getDeviceById")
        .includes(uuid) &&
      !!deviceApi.endpoints.getDeviceById.select(uuid)(state).data
    );
  };

  const firstLoading = (uuid: string) => {
    const state = getState();
    const loading = (entry: {
      isLoading: boolean;
      data?: unknown;
      error?: unknown;
    }) => entry.isLoading && !entry.data && !entry.error;
    return (
      deviceApi.util
        .selectCachedArgsForQuery(state, "listDevices")
        .some((arg) =>
          loading(deviceApi.endpoints.listDevices.select(arg)(state)),
        ) || loading(deviceApi.endpoints.getDeviceById.select(uuid)(state))
    );
  };

  const patch = (event: DeviceStateEnvelope) => {
    const uuid = event.subject.device;
    listArgsHolding(uuid).forEach((arg) =>
      dispatch(
        deviceApi.util.updateQueryData("listDevices", arg, (draft) => {
          const device = draft.items.find((d) => d.uuid === uuid);
          if (device) applyTo(device, event);
        }),
      ),
    );
    if (detailHolding(uuid)) {
      dispatch(
        deviceApi.util.updateQueryData("getDeviceById", uuid, (draft) =>
          applyTo(draft, event),
        ),
      );
    }
  };

  const handle = (event: DeviceStateEnvelope) => {
    const uuid = event.subject.device;

    const pending = held.get(uuid);
    if (pending) {
      if (event.data.version > pending.event.data.version) {
        // The device came back, or moved on, before the OFF ever showed.
        clearTimeout(pending.timer);
        held.delete(uuid);
      } else if (event.data.state === OFF_STATE) {
        // A duplicate of the held OFF, or an older one it already supersedes.
        return;
      }
      // Otherwise an older, late event: it applies on its own merits below,
      // and the held OFF still follows it.
    }

    if (firstLoading(uuid)) {
      dispatch(deviceApi.util.invalidateTags(["Device"]));
    }

    if (listArgsHolding(uuid).length === 0 && !detailHolding(uuid)) return;

    if (event.data.state === OFF_STATE) {
      held.set(uuid, {
        event,
        timer: setTimeout(() => {
          held.delete(uuid);
          patch(event);
        }, OFF_DEBOUNCE_MS),
      });
      return;
    }

    patch(event);
  };

  const dispose = () => {
    held.forEach(({ timer }) => clearTimeout(timer));
    held.clear();
  };

  return { handle, dispose };
};
